import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, SortingState, flexRender } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';

import {
  ManagementSupportedDtoIn,
  ManagementSupportedClassificationDtoIn,
  SupportedsQueryParams,
  RemoveSupportedFromFundResult,
} from '@/models/in/ManagementSupportedDtoIn';
import { getAuth, postAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi, usePostAuthApiInfiniteQuery, PaginatedResponse } from '@/hooks/UseApiCalls';
import {
  ISRAELI_ID_CHANGE_REQUESTS_URL,
  SupportedIsraeliIdChangeRequestDtoIn,
} from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import useTableDensity from '@/hooks/useTableDensity';
import { ExpirationStatus } from '@/helpers/contactClassificationExpiration';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import ListShortenedByCount from '@/components/ui/ListShortenedByCount/ListShortenedByCount';
import TintedIcon from '@/components/ui/TintedIcon/TintedIcon';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import EditBankAccountDialog from './EditBankAccountDialog/EditBankAccountDialog';
import SupportedEditDialog from './SupportedEditDialog/SupportedEditDialog';
import RemoveFromFundDialog from './RemoveFromFundDialog/RemoveFromFundDialog';
import SupportedChildrenDialog from './SupportedChildrenDialog/SupportedChildrenDialog';
import FiltersArea from './FiltersArea';
import ReviewIsraeliIdChangeRequestDialog from './IsraeliIdChangeRequest/ReviewIsraeliIdChangeRequestDialog';
import IsraeliIdChangeRequestsBanner, { IsraeliIdChangeDecision } from './IsraeliIdChangeRequest/IsraeliIdChangeRequestsBanner';
import IsraeliIdChangeDot from '@/pages/Supporteds/IsraeliIdChange/IsraeliIdChangeDot';
import useSeenIsraeliIdChangeAlerts from '@/hooks/useSeenIsraeliIdChangeAlerts';
import SupportCertificatePopup from '@/pages/Supporteds/SupportCertificatePopup/SupportCertificatePopup';
import certificateIcon from '@/assets/icons/support-certificate.svg';
import childrenIcon from '@/assets/icons/children.svg';
import paymentIcon from '@/assets/icons/payment-card.svg';
import editIcon from '@/assets/icons/edit.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import styles from './SupportedsManagementPage.module.css';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const DENSITY_STORAGE_KEY = 'table.density.supportedsManagement';

// The export fetches every matching row in one call, not just the pages already scrolled into view.
// Matches the server's maximum page length.
const EXPORT_PAGE_LENGTH = 100000;

// Map column IDs to backend sort field names
const SORT_FIELD_MAP: Record<string, string> = {
  supported: 'ContactName',
  email: 'Email',
  phone: 'Phone',
};

const SupportedsManagementPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const [selectedRow, setSelectedRow] = useState<ManagementSupportedDtoIn>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  // Page size state
  const [pageSize, setPageSize] = useState<number>(50);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);

  // Classification filters
  const [selectedClassifications, setSelectedClassifications] = useState<string[]>([]);
  const [selectedExpiration, setSelectedExpiration] = useState<ExpirationStatus | null>(null);

  // State to store row identifier for reopening blade after refresh
  const [pendingRowId, setPendingRowId] = useState<number | null>(null);

  // Edit bank account dialog state
  const [isEditBankAccountDialogOpen, setIsEditBankAccountDialogOpen] = useState(false);
  const [supportedToEditBankAccount, setSupportedToEditBankAccount] = useState<ManagementSupportedDtoIn | null>(null);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'supported', desc: false }]);

  // Supported edit dialog state
  const [isSupportedEditDialogOpen, setIsSupportedEditDialogOpen] = useState(false);
  const [supportedToEditDetails, setSupportedToEditDetails] = useState<ManagementSupportedDtoIn | null>(null);

  // Remove from fund dialog state
  const [isRemoveFromFundDialogOpen, setIsRemoveFromFundDialogOpen] = useState(false);
  const [supportedToRemoveFromFund, setSupportedToRemoveFromFund] = useState<ManagementSupportedDtoIn | null>(null);

  // Children dialog state
  const [supportedForChildren, setSupportedForChildren] = useState<ManagementSupportedDtoIn | null>(null);

  // Support certificate (all funds) dialog state
  const [supportedForCertificate, setSupportedForCertificate] = useState<ManagementSupportedDtoIn | null>(null);

  // HAT-1428: coordinators' requests to correct a supported's ID are decided here, on the supported -
  // a dot beside the name, a filter chip, a line above the board said once, the request on the
  // blade - the way the coordinator's board shows them (Chaya, 20.10). The sidebar's count reads
  // the same query.
  const { data: pendingIdChanges } = useGetAuthApi<SupportedIsraeliIdChangeRequestDtoIn[]>(
    ISRAELI_ID_CHANGE_REQUESTS_URL,
    undefined,
    { staleTime: 30000, refetchInterval: 60000 }
  );
  const pendingIdChangeBySupported = useMemo(
    () => new Map((pendingIdChanges ?? []).map((r) => [r.contactId, r])),
    [pendingIdChanges]
  );
  const [onlyPendingIdChanges, setOnlyPendingIdChanges] = useState(false);
  const [reviewingIdChange, setReviewingIdChange] = useState<SupportedIsraeliIdChangeRequestDtoIn | null>(null);
  const [lastIdChangeDecision, setLastIdChangeDecision] = useState<IsraeliIdChangeDecision | null>(null);
  const { unseen: unseenIdChanges, markSeen: markIdChangesSeen } = useSeenIsraeliIdChangeAlerts('management');
  const idChangesToTell = unseenIdChanges(pendingIdChanges ?? []);

  // The chip goes with the last waiting request, and so does its filter
  useEffect(() => {
    if (onlyPendingIdChanges && pendingIdChanges && pendingIdChanges.length === 0) setOnlyPendingIdChanges(false);
  }, [onlyPendingIdChanges, pendingIdChanges]);

  // Build query params (uses debounced search for API calls)
  const queryParams: SupportedsQueryParams = useMemo(
    () => ({
      sortBy: SORT_FIELD_MAP[sorting[0]?.id] || 'ContactName',
      sortDirection: sorting[0]?.desc ? 'Desc' : 'Asc',
      searchTerm: debouncedSearch || undefined,
      fundId: selectedFundId ?? undefined,
      classificationFilters: selectedClassifications,
      expirationFilter: selectedExpiration ?? undefined,
      onlyPendingIsraeliIdChangeRequests: onlyPendingIdChanges || undefined,
    }),
    [sorting, debouncedSearch, selectedFundId, selectedClassifications, selectedExpiration, onlyPendingIdChanges]
  );

  const {
    data: response,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePostAuthApiInfiniteQuery<ManagementSupportedDtoIn, SupportedsQueryParams>('management/supporteds', queryParams, pageSize);

  const accumulatedData = useMemo(() => response?.pages.flatMap((page) => page.data) ?? [], [response]);
  const classificationCounts = response?.pages[0]?.classificationCounts ?? {};
  const expirationCounts = response?.pages[0]?.expirationCounts ?? {};

  // Refetch data when page size changes
  useEffect(() => {
    refetch();
  }, [pageSize, refetch]);

  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const resourceId = params.get('resourceId');
    if (!resourceId || !currentUser?.token) return;
    deepLinkHandled.current = true;
    const headers = { Authorization: `Bearer ${currentUser.token}` };
    getAuth<ManagementSupportedDtoIn>(`management/supporteds/${resourceId}`, headers)
      .then(item => {
        if (!item) return;
        setSelectedRow(item);
        // Narrow the table to that one supported, so its row actions (edit, «הסר מפרויקט») are
        // right there - the ID-change dialog sends the office here to act on this card (HAT-1428)
        if (item.israeliId) setSearchQuery(item.israeliId);
      })
      .catch(() => {})
      .finally(() => window.history.replaceState({}, '', window.location.pathname));
  }, [currentUser?.token]);

  // Handle blade reopening after refresh and update selectedRow when data changes
  useEffect(() => {
    if (accumulatedData.length === 0) return;

    // Case 1: Reopen blade after refresh
    if (pendingRowId) {
      const rowToReopen = accumulatedData.find((row) => row.id === pendingRowId);
      if (rowToReopen) {
        setSelectedRow(rowToReopen);
      }
      setPendingRowId(null);
      return;
    }

    // Case 2: Update blade data after filter/pagination changes
    if (selectedRow) {
      const updatedRow = accumulatedData.find((row) => row.id === selectedRow.id);
      if (updatedRow) {
        // Only update if data actually changed
        if (JSON.stringify(updatedRow) !== JSON.stringify(selectedRow)) {
          setSelectedRow(updatedRow);
        }
      }
      // Don't clear selectedRow here - the row might be in a page that isn't loaded yet
      // Only clear it when user explicitly closes the blade
    }
  }, [accumulatedData, pendingRowId, selectedRow]);

  const handleRowClick = useCallback((row: ManagementSupportedDtoIn) => {
    setSelectedRow(row);
  }, []);

  const handleCloseBlade = useCallback(() => {
    setSelectedRow(undefined);
  }, []);

  const handleRefresh = useCallback(() => {
    // Save the row identifier before closing for reopening later
    if (selectedRow) {
      setPendingRowId(selectedRow.id);
    }
    setSelectedRow(undefined); // Close the blade immediately
    refetch();
  }, [refetch, selectedRow]);

  const handleOpenEditBankAccountDialog = useCallback((supported: ManagementSupportedDtoIn) => {
    setSupportedToEditBankAccount(supported);
    setIsEditBankAccountDialogOpen(true);
  }, []);

  const handleCloseEditBankAccountDialog = useCallback(() => {
    setIsEditBankAccountDialogOpen(false);
    setSupportedToEditBankAccount(null);
  }, []);

  const handleEditBankAccountSuccess = useCallback(() => {
    const editedSupportedId = supportedToEditBankAccount?.id;
    setIsEditBankAccountDialogOpen(false);
    setSupportedToEditBankAccount(null);

    // Invalidate payment infos cache after dialog closes (exact URL match only)
    if (editedSupportedId) {
      const targetUrl = `management/supporteds/${editedSupportedId}/payment-infos`;
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0] as string;
          return key === targetUrl;
        }
      });
    }

    // The payment infos count is shown in the table, so the list has to catch up too
    refetch();
  }, [queryClient, refetch, supportedToEditBankAccount?.id]);

  // Supported edit dialog handlers
  const handleOpenSupportedEditDialog = useCallback((supported: ManagementSupportedDtoIn) => {
    setSupportedToEditDetails(supported);
    setIsSupportedEditDialogOpen(true);
  }, []);

  const handleCloseSupportedEditDialog = useCallback(() => {
    setIsSupportedEditDialogOpen(false);
    setSupportedToEditDetails(null);
  }, []);

  const handleSupportedEditSuccess = useCallback(() => {
    handleCloseSupportedEditDialog();
    refetch();
  }, [handleCloseSupportedEditDialog, refetch]);

  // Remove from fund dialog handlers
  const handleOpenRemoveFromFundDialog = useCallback((supported: ManagementSupportedDtoIn) => {
    setSupportedToRemoveFromFund(supported);
    setIsRemoveFromFundDialogOpen(true);
  }, []);

  const handleCloseRemoveFromFundDialog = useCallback(() => {
    setIsRemoveFromFundDialogOpen(false);
    setSupportedToRemoveFromFund(null);
  }, []);

  const handleRemoveFromFundSuccess = useCallback((result?: RemoveSupportedFromFundResult) => {
    // Removing the last fund deletes the supported, so a blade still showing it has to close
    if (result?.supportedArchived && selectedRow?.id === supportedToRemoveFromFund?.id) {
      setSelectedRow(undefined);
    }
    refetch();
  }, [refetch, selectedRow?.id, supportedToRemoveFromFund?.id]);

  // Sorting change handler
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  /** Expiration date of a classification, or its pending state when it has no date yet. */
  const formatClassificationExpiration = (classification?: ManagementSupportedClassificationDtoIn) => {
    if (!classification) return '';

    if (classification.status === ContactClassificationStatus.PendingApproval) {
      return t('pendingApproval');
    }

    if (!classification.expiresTimestamp) return '-';

    return new Date(classification.expiresTimestamp).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  /** A row action shown as an icon button, so the common actions take one click instead of two. */
  const renderIconAction = (icon: string, label: string, onClick: () => void) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={styles.iconAction}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <TintedIcon icon={icon} size={24} />
    </button>
  );

  const iconActionColumnMeta = {
    className: 'max-w-[48px] min-w-[48px] w-[48px]',
    style: { textAlign: 'center' as const },
    lockLayout: true,
  };

  const columns: ColumnDef<ManagementSupportedDtoIn>[] = [
    {
      id: 'supported',
      header: Col(t('supported')),
      accessorFn: (row) => ({
        firstName: row.firstName,
        lastName: row.lastName,
        israeliId: row.israeliId,
      }),
      cell: (context) => {
        const idChange = pendingIdChangeBySupported.get(context.row.original.id);
        return (
          <div className="flex items-center gap-[6px] min-w-0">
            <div className="min-w-0">
              {flexRender(Cell<ManagementSupportedDtoIn>(cellType.SupportedWithIsraeliID), context)}
            </div>
            {idChange && (
              <IsraeliIdChangeDot
                request={idChange}
                label={t('israeliIdChangeRequests.dot', { id: idChange.requestedIsraeliId })}
                onClick={() => setReviewingIdChange(idChange)}
              />
            )}
          </div>
        );
      },
      enableSorting: true,
      meta: {
        className: 'max-w-[16%] min-w-[16%] w-[16%]',
        mobileClassName: 'max-w-[40%] min-w-[40%] w-[40%]',
      },
    },
    {
      id: 'classification',
      header: Col(t('classification')),
      enableSorting: false,
      cell: ({ row }) => {
        const classifications = row.original.contactClassifications;
        if (classifications.length === 0) return <div></div>;

        // Classifications belong to a fund, so without a fund filter a supported can show the same
        // classification more than once - the tooltip names the fund of each one.
        const tooltipContent = classifications.length > 1
          ? (
              <div className="body-xs-sm-regular font-body-1">
                {classifications.map((classification) => `${t(classification.name)} (${classification.fundId})`).join(', ')}
              </div>
            )
          : null;

        return (
          <ListShortenedByCount
            value={t(classifications[0].name)}
            count={classifications.length - 1}
            tooltipId={classifications.length > 1 ? `supported-classifications-${row.original.id}` : undefined}
            tooltipContent={tooltipContent}
          />
        );
      },
      meta: {
        className: 'max-w-[12%] min-w-[12%] w-[12%]',
        mobileClassName: 'max-w-[22%] min-w-[22%] w-[22%]',
      },
    },
    {
      id: 'classificationExpiration',
      header: Col(t('classificationExpiration')),
      enableSorting: false,
      // The server sorts a supported's classifications approved-first then by nearest expiry, so the
      // first one is the one worth showing here.
      accessorFn: (row) => formatClassificationExpiration(row.contactClassifications[0]),
      cell: Cell(cellType.String),
      meta: {
        className: 'max-w-[11%] min-w-[11%] w-[11%]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'childrenCount',
      header: Col(t('childrenCount')),
      accessorKey: 'childrenCount',
      cell: Cell(cellType.Number),
      enableSorting: false,
      meta: {
        className: 'max-w-[12%] min-w-[12%] w-[12%]',
        style: { textAlign: 'center' as const },
        mobileClassName: 'max-w-[18%] min-w-[18%] w-[18%]',
      },
    },
    {
      id: 'email',
      header: Col(t('email')),
      accessorKey: 'email',
      cell: Cell(cellType.String),
      enableSorting: true,
      meta: {
        className: 'max-w-[16%] min-w-[16%] w-[16%]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'phone',
      header: Col(t('phone')),
      accessorKey: 'phone',
      cell: Cell(cellType.String),
      enableSorting: true,
      meta: {
        className: 'max-w-[10%] min-w-[10%] w-[10%]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'funds',
      header: Col(t('funds')),
      enableSorting: false,
      accessorFn: (row) => ({
        items: row.fundIds ?? [],
        tooltipId: `supported-funds-${row.id}`,
      }),
      cell: Cell(cellType.TruncatedList),
      meta: {
        className: 'max-w-[9%] min-w-[9%] w-[9%]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'supportCertificateAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => renderIconAction(
        certificateIcon,
        t('supportCertificate.title'),
        () => setSupportedForCertificate(row.original)
      ),
      meta: { ...iconActionColumnMeta, hiddenOnMobile: true },
    },
    {
      id: 'childrenAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => renderIconAction(
        childrenIcon,
        t('children'),
        () => setSupportedForChildren(row.original)
      ),
      meta: { ...iconActionColumnMeta, hiddenOnMobile: true },
    },
    {
      id: 'paymentInfoAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => renderIconAction(
        paymentIcon,
        t('paymentDetails'),
        () => handleOpenEditBankAccountDialog(row.original)
      ),
      meta: { ...iconActionColumnMeta, hiddenOnMobile: true },
    },
    {
      id: 'editAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => renderIconAction(
        editIcon,
        t('supportedsManagement.editSupportedDetails'),
        () => handleOpenSupportedEditDialog(row.original)
      ),
      meta: iconActionColumnMeta,
    },
    {
      id: 'removeFromFundAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => renderIconAction(
        deleteIcon,
        t('supportedsManagement.removeFromFund'),
        () => handleOpenRemoveFromFundDialog(row.original)
      ),
      meta: { ...iconActionColumnMeta, hiddenOnMobile: true },
    },
  ];

  const exportColumns: ColumnDef<ManagementSupportedDtoIn>[] = [
    {
      id: 'supported',
      header: 'supported',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      id: 'israeliId',
      header: 'israeliId',
      accessorKey: 'israeliId',
    },
    {
      id: 'email',
      header: 'email',
      accessorKey: 'email',
    },
    {
      id: 'phone',
      header: 'phone',
      accessorKey: 'phone',
    },
    {
      id: 'funds',
      header: 'funds',
      accessorFn: (row) => (row.fundIds ?? []).join(', '),
    },
    {
      id: 'classification',
      header: 'classification',
      accessorFn: (row) => row.contactClassifications.map((classification) => t(classification.name)).join(', '),
    },
    {
      id: 'classificationExpiration',
      header: 'classificationExpiration',
      // One entry per classification, in the same order as the classification column, so the two
      // columns stay aligned even when a classification has no expiration date.
      accessorFn: (row) => row.contactClassifications.map(formatClassificationExpiration).join(', '),
    },
    {
      id: 'childrenCount',
      header: 'childrenCount',
      accessorKey: 'childrenCount',
    },
    {
      id: 'paymentInfosCount',
      header: 'paymentInfosCount',
      accessorKey: 'paymentInfosCount',
    },
  ];

  /**
   * Exports every supported matching the current filters, not only the pages already loaded into
   * the table by endless scrolling.
   */
  const getExportData = async (): Promise<ManagementSupportedDtoIn[]> => {
    const headers = { Authorization: `Bearer ${currentUser?.token}` };
    const response = await postAuth<PaginatedResponse<ManagementSupportedDtoIn>, SupportedsQueryParams & { start: number; length: number }>(
      'management/supporteds',
      headers,
      { ...queryParams, start: 0, length: EXPORT_PAGE_LENGTH }
    );

    return response.data;
  };

  return (
    <div>
      {selectedRow && (
        <AdditionalInfo
          data={selectedRow}
          handleXclick={handleCloseBlade}
          onRefresh={handleRefresh}
          onEditPaymentInfos={() => handleOpenEditBankAccountDialog(selectedRow)}
          onEditChildren={() => setSupportedForChildren(selectedRow)}
          onReviewIdChangeRequest={setReviewingIdChange}
        />
      )}
      {isEditBankAccountDialogOpen && supportedToEditBankAccount && (
        <EditBankAccountDialog
          open={isEditBankAccountDialogOpen}
          onClose={handleCloseEditBankAccountDialog}
          supported={supportedToEditBankAccount}
          onSuccess={handleEditBankAccountSuccess}
        />
      )}
      <DataTable
        title={t('supportedsManagement.title')}
        data={accumulatedData}
        notice={
          lastIdChangeDecision || idChangesToTell.length > 0 ? (
            <IsraeliIdChangeRequestsBanner
              pending={idChangesToTell}
              onShow={() => setOnlyPendingIdChanges(true)}
              onSeen={markIdChangesSeen}
              lastDecision={lastIdChangeDecision}
              onDismissDecision={() => setLastIdChangeDecision(null)}
            />
          ) : undefined
        }
        isLoading={isLoading}
        columns={columns}
        exportColumns={exportColumns}
        getExportData={getExportData}
        exportFilename={t('supportedsManagement.title')}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="supportedsManagement"
        searchPlaceholder="supportedsManagement.searchSupported"
        searchTooltip="searchSupportedTooltip"
        sortBy={sorting[0]}
        filters={
          <FiltersArea
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedFundId={selectedFundId}
            onFundChange={setSelectedFundId}
            classificationCounts={classificationCounts}
            selectedClassifications={selectedClassifications}
            onClassificationsChange={setSelectedClassifications}
            expirationCounts={expirationCounts}
            selectedExpiration={selectedExpiration}
            onExpirationChange={setSelectedExpiration}
            idChangeFilter={{
              count: pendingIdChanges?.length ?? 0,
              isSelected: onlyPendingIdChanges,
              onToggle: () => setOnlyPendingIdChanges((on) => !on),
            }}
          />
        }
        customGlobalSearch={() => true}
        handleRowClick={handleRowClick}
        activeRecord={selectedRow}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        manualSorting={true}
        onSortingChange={handleSortingChange}
      />

      {/* Edit Supported Dialog */}
      {isSupportedEditDialogOpen && supportedToEditDetails && (
        <SupportedEditDialog
          open={isSupportedEditDialogOpen}
          onClose={handleCloseSupportedEditDialog}
          supported={supportedToEditDetails}
          onSuccess={handleSupportedEditSuccess}
          onReviewPendingRequest={pendingIdChangeBySupported.has(supportedToEditDetails.id)
            ? () => {
              setReviewingIdChange(pendingIdChangeBySupported.get(supportedToEditDetails.id) ?? null);
              handleCloseSupportedEditDialog();
            }
            : undefined}
        />
      )}

      {/* Remove From Fund Dialog */}
      {isRemoveFromFundDialogOpen && supportedToRemoveFromFund && (
        <RemoveFromFundDialog
          open={isRemoveFromFundDialogOpen}
          onClose={handleCloseRemoveFromFundDialog}
          supported={supportedToRemoveFromFund}
          onSuccess={handleRemoveFromFundSuccess}
        />
      )}

      {/* Children Dialog */}
      {supportedForChildren && (
        <SupportedChildrenDialog
          open={!!supportedForChildren}
          supported={supportedForChildren}
          onClose={() => setSupportedForChildren(null)}
          onRefreshList={refetch}
        />
      )}

      {/* A coordinator's request to correct a supported's ID, from its dot, the blade or the edit */}
      {reviewingIdChange && (
        <ReviewIsraeliIdChangeRequestDialog
          request={reviewingIdChange}
          onClose={() => setReviewingIdChange(null)}
          onDecided={(decision, emailSent) => {
            setLastIdChangeDecision({ decision, request: reviewingIdChange, emailSent });
            setReviewingIdChange(null);
            // The waiting list (dots, line, sidebar count) and the blade's record all start with it
            queryClient.invalidateQueries({
              predicate: (query) => String(query.queryKey[0]).startsWith(ISRAELI_ID_CHANGE_REQUESTS_URL),
            });
            // An approval changed the ID shown on the row and the blade
            refetch();
          }}
        />
      )}

      {/* Support Certificate Dialog - every fund the supported was paid from */}
      {supportedForCertificate && (
        <SupportCertificatePopup
          supportedId={supportedForCertificate.id}
          supportedName={`${supportedForCertificate.firstName} ${supportedForCertificate.lastName}`.trim()}
          open={!!supportedForCertificate}
          setOpen={(isOpen) => {
            if (!isOpen) setSupportedForCertificate(null);
          }}
        />
      )}
    </div>
  );
};

export default SupportedsManagementPage;
